import { useState, useMemo } from 'react';
import { X, Plus, FolderOpen, FileText } from 'lucide-react';
import {
  useDocumentTemplates,
  useCreateDocumentTemplateCategory,
  useCreateDocumentTemplate,
} from '../../hooks/useDocumentTemplates';
import type { DocumentTemplate } from '../../services/documentTemplateService';
import toast from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  projectId: string;
  onClose: () => void;
  onSelect: (template: { name: string; content?: unknown }) => void;
}

export default function DocumentTemplateSelectModal({ isOpen, projectId, onClose, onSelect }: Props) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');

  const { data: categories, isLoading } = useDocumentTemplates(projectId);
  const createCategoryMut = useCreateDocumentTemplateCategory(projectId);
  const createTemplateMut = useCreateDocumentTemplate(projectId);

  const filteredTemplates = useMemo(() => {
    if (!categories) return [];
    if (!selectedCategoryId) return categories.flatMap((c) => c.templates);
    const cat = categories.find((c) => c.categoryId === selectedCategoryId);
    return cat?.templates ?? [];
  }, [categories, selectedCategoryId]);

  const handleCreateCategory = () => {
    if (!newCategoryName.trim()) return;
    createCategoryMut.mutate(
      { name: newCategoryName.trim() },
      {
        onSuccess: () => {
          setNewCategoryName('');
          setShowNewCategory(false);
          toast.success('카테고리가 생성되었습니다.');
        },
        onError: (err: any) => toast.error(err?.response?.data?.error ?? '생성 실패'),
      },
    );
  };

  const handleCreateTemplate = () => {
    if (!newTemplateName.trim()) return;
    const categoryId = selectedCategoryId ?? categories?.[0]?.categoryId;
    if (!categoryId) {
      toast.error('먼저 카테고리를 생성해주세요.');
      return;
    }
    createTemplateMut.mutate(
      { categoryId, name: newTemplateName.trim(), description: newTemplateDesc.trim() || undefined },
      {
        onSuccess: () => {
          setNewTemplateName('');
          setNewTemplateDesc('');
          setShowNewTemplate(false);
          toast.success('템플릿이 생성되었습니다.');
        },
        onError: (err: any) => toast.error(err?.response?.data?.error ?? '생성 실패'),
      },
    );
  };

  const handleSelect = (template: DocumentTemplate) => {
    onSelect({ name: template.name, content: template.content });
    onClose();
  };

  const resetState = () => {
    setSelectedCategoryId(null);
    setShowNewCategory(false);
    setNewCategoryName('');
    setShowNewTemplate(false);
    setNewTemplateName('');
    setNewTemplateDesc('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-xl w-[640px] max-w-[95vw] max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
          <h3 className="text-sm font-semibold text-gray-900">템플릿에서 선택</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="size-4" />
          </button>
        </div>

        {/* Body — 2패널 */}
        <div className="flex flex-1 min-h-0">
          {/* 좌: 카테고리 */}
          <div className="w-[180px] shrink-0 border-r border-gray-200 overflow-y-auto p-3 space-y-1">
            <button
              onClick={() => setSelectedCategoryId(null)}
              className={`w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors ${
                !selectedCategoryId ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              전체
            </button>

            {categories?.map((cat) => (
              <button
                key={cat.categoryId}
                onClick={() => setSelectedCategoryId(cat.categoryId)}
                className={`w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                  selectedCategoryId === cat.categoryId ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <FolderOpen className="size-3.5 shrink-0" />
                <span className="truncate">{cat.name}</span>
                <span className="text-[11px] text-gray-400 ml-auto">{cat.templates.length}</span>
              </button>
            ))}

            {/* 카테고리 추가 */}
            {showNewCategory ? (
              <div className="space-y-1 pt-1">
                <input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                  placeholder="카테고리 이름"
                  autoFocus
                  className="w-full px-2 py-1 text-sm border border-gray-200 rounded outline-none focus:ring-1 focus:ring-blue-500"
                />
                <div className="flex gap-1">
                  <button onClick={handleCreateCategory} disabled={createCategoryMut.isPending}
                    className="px-2 py-0.5 text-[11px] text-white bg-blue-600 rounded disabled:opacity-50">생성</button>
                  <button onClick={() => { setShowNewCategory(false); setNewCategoryName(''); }}
                    className="px-2 py-0.5 text-[11px] text-gray-500 hover:text-gray-700">취소</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowNewCategory(true)}
                className="flex items-center gap-1 px-2 py-1.5 text-sm text-blue-600 hover:text-blue-800 w-full"
              >
                <Plus className="size-3" />
                카테고리 추가
              </button>
            )}
          </div>

          {/* 우: 템플릿 목록 */}
          <div className="flex-1 overflow-y-auto p-3">
            {isLoading ? (
              <p className="text-sm text-gray-400 py-8 text-center">로딩 중...</p>
            ) : filteredTemplates.length === 0 && !showNewTemplate ? (
              <div className="text-center py-8">
                <FileText className="size-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">아직 템플릿이 없습니다.</p>
                <p className="text-sm text-gray-400 mt-1">새 템플릿을 만들어보세요.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTemplates.map((t) => (
                  <button
                    key={t.templateId}
                    onClick={() => handleSelect(t)}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-900">{t.name}</p>
                    {t.description && <p className="text-sm text-gray-500 mt-0.5">{t.description}</p>}
                  </button>
                ))}
              </div>
            )}

            {/* 인라인 템플릿 생성 */}
            {showNewTemplate ? (
              <div className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2">
                <input
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="템플릿 이름 *"
                  autoFocus
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded outline-none focus:ring-1 focus:ring-blue-500"
                />
                <input
                  value={newTemplateDesc}
                  onChange={(e) => setNewTemplateDesc(e.target.value)}
                  placeholder="설명 (선택)"
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded outline-none focus:ring-1 focus:ring-blue-500"
                />
                <div className="flex gap-2">
                  <button onClick={handleCreateTemplate} disabled={createTemplateMut.isPending || !newTemplateName.trim()}
                    className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded disabled:opacity-50">생성</button>
                  <button onClick={() => { setShowNewTemplate(false); setNewTemplateName(''); setNewTemplateDesc(''); }}
                    className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700">취소</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowNewTemplate(true)}
                className="flex items-center gap-1 mt-3 px-2 py-1.5 text-sm text-blue-600 hover:text-blue-800"
              >
                <Plus className="size-3" />
                새 템플릿 만들기
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
